import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { t } from 'ember-intl';
import {
  SMART_INSIGHTS_LIST_SORT_OPTIONS,
  SMART_INSIGHTS_LIST_SORT_OPTIONS_TRANSLATIONS,
  type SmartInsightsListSortOption,
} from 'lumen/utils/constants/smart-insights';

interface GeneralSmartInsightsListDropdownSortSignature {
  Args: {
    setSmartInsightsListSortOption: (
      sortOption: SmartInsightsListSortOption,
    ) => void;
    smartInsightsListSortOption: SmartInsightsListSortOption;
  };
  Element: HTMLDivElement;
}

type DropdownSortOption = Option & {
  label: string;
  value: SmartInsightsListSortOption;
};

export default class GeneralSmartInsightsListDropdownSort extends Component<GeneralSmartInsightsListDropdownSortSignature> {
  @service declare intl: Services['intl'];

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get options(): DropdownSortOption[] {
    return Object.values(SMART_INSIGHTS_LIST_SORT_OPTIONS).map((option) => ({
      label: this.intl.t(SMART_INSIGHTS_LIST_SORT_OPTIONS_TRANSLATIONS[option]),
      value: option,
    }));
  }

  get selectedOption(): DropdownSortOption | undefined {
    const { smartInsightsListSortOption } = this.args;

    return this.options.find(
      (option) => option.value === smartInsightsListSortOption,
    );
  }

  @action onChangeSort(selectedOption: DropdownSortOption): void {
    this.args.setSmartInsightsListSortOption(selectedOption.value);
  }

  <template>
    <div
      class="flex flex-row items-center gap-100"
      data-test-id="dropdown-sort"
    >
      <span class="text-200--regular">{{t "general.sort_by"}}</span>

      <PrismSelectInput
        @isSmall={{true}}
        @onChange={{this.onChangeSort}}
        @options={{this.options}}
        @value={{this.selectedOption}}
      />
    </div>
  </template>
}
